// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import { AppBar, Button, Tab, Tabs } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import BackdropProgress from "../../BackdropProgress";
import Box from "@mui/material/Box";
import Costs from "../cost/Costs";
import CustomSnackbar from "../ui/Snackbars";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HotelBooking from "../bookings/HotelBooking";
import Invoices from "../invoice/Invoices";
import PerdiemInformation from "../cost/PerdiemInformation";
import TravelInformation from "./TravelInformation";
import PerDiemPayment from "../cost/PerDiemPayment";
import useHttp from "../../utils/http";
import wso2Theme from "../../theme";
import { useParams } from "react-router-dom";
import {
  getTravelAgents,
  postSearchVisas,
  getTravelRequest,
  getTravelRequstAgentVisaByJobNumber,
  getEmployeeVisaById,
  postValidateAgentTravel,
} from "../../utils/end-points";
import { isAccessGranted } from "../../utils/oauth";
import {
  NO_VALID_VISA_AVAILABLE_ID,
  PRIVILEGES,
  VISA_NOT_APPLICABLE_ID,
} from "../../Config.js";
import { useEffect, useState, useCallback } from "react";
import "../../css/ManageTravelRequest.scss";
import TravelCard from "../cost/TravelCard.js";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageTravelRequest = () => {
  const { jobNumber } = useParams();
  const { handleRequest } = useHttp();
  const [travelInfoAccordion, setTravelInfoAccordion] = useState(true);
  const [perDiemPaymentsAccordion, setPerDiemPaymentsAccordion] =
    useState(true);
  const [travelRequest, setTravelRequest] = useState(null);
  const [perDiemAccordion, setPerDiemAccordion] = useState(true);
  const [visa, setVisa] = useState([]);
  const [travelAgent, setTravelAgent] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [isChildrenRenderEnabled, setIsChildrenRenderEnabled] = useState(false);
  const [travelCardAccordion, setTravelCardAccordion] = useState(true);
  const [invoiceAccordion, setInvoiceAccordion] = useState(true);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });
  const [tabBarValue, setTabBarValue] = useState(0);
  const [isFetchingPerdiemPaymentSummary, setIsFetchingPerdiemPaymentSummary] = useState(false);

  useEffect(() => {
    fetchData(false);
  }, []);

  const handleSetLoading = useCallback((loadingState) => {
    setLoading(loadingState);
  }, []); 

  const fetchData = useCallback(async (showSuccessSnack) => {
    const promises = [];
    let travellerEmail = null;
    let visaId = null;
    promises.push(
      new Promise(function (resolve, reject) {
        handleRequest(
          getTravelAgents + "?status=all",
          "GET",
          null,
          (travelAgents) => {
            if (travelAgents) {
              setTravelAgent(travelAgents);
              resolve();
            } else {
              setSnackbarData({
                message: "Error while fetching travel agents",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          },
          (error) => {
            setSnackbarData({
              message: error,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            reject();
          }
        );
      })
    );

    // Get travel request information for users having access to view all travel requests and view own travel request
    if (
      isAccessGranted([
        PRIVILEGES.ALL_TRAVEL_REQUESTS,
        PRIVILEGES.VIEW_OWN_TRAVEL_REQUEST,
      ])
    ) {
      promises.push(
        new Promise(function (resolve, reject) {
          handleRequest(
            getTravelRequest.replace("$jobNumber", jobNumber),
            "GET",
            null,
            (travel) => {
              if (travel) {
                travellerEmail = travel.travellerEmail;
                visaId = travel.visaId;
                setTravelRequest(travel);
                resolve();
              } else {
                setSnackbarData({
                  message: "Error while fetching travel requests",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
                reject();
              }
            },
            (error) => {
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          );
        })
      );
    }
    // Get travel request information by validating user access for the travel request
    else if (
      isAccessGranted([PRIVILEGES.AGENT_TRAVEL_REQUESTS]) &&
      visaId !== NO_VALID_VISA_AVAILABLE_ID.id &&
      visaId !== VISA_NOT_APPLICABLE_ID.id
    ) {
      promises.push(
        new Promise(function (resolve, reject) {
          handleRequest(
            postValidateAgentTravel.replace("$jobNumber", jobNumber),
            "POST",
            null,
            (travel) => {
              if (travel) {
                travellerEmail = travel.travellerEmail;
                visaId = travel.visaId;
                setTravelRequest(travel);
                resolve();
              } else {
                setSnackbarData({
                  message: "Error while fetching travel requests",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
                reject();
              }
            },
            (error) => {
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          );
        })
      );
    } else {
      setSnackbarData({
        message: "You don't have access for this page!",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
    }

    Promise.all(promises)
      .then(() => {
        if (isAccessGranted([PRIVILEGES.ALL_TRAVEL_REQUESTS])) {
          let body = {
            status: "active",
            offset: 0,
            email: travellerEmail,
          };

          handleRequest(
            postSearchVisas,
            "POST",
            body,
            (visas) => {
              if (visas) {
                setVisa(visas);
              } else {
                setSnackbarData({
                  message: "Error while fetching employee visa",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
              }
              setIsChildrenRenderEnabled(true);
              handleSetLoading(false);
            },
            (error) => {
              setIsChildrenRenderEnabled(true);
              handleSetLoading(false);
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
            }
          );
        } else if (
          isAccessGranted([PRIVILEGES.AGENT_TRAVEL_REQUESTS]) &&
          visaId !== NO_VALID_VISA_AVAILABLE_ID.id &&
          visaId !== VISA_NOT_APPLICABLE_ID.id &&
          visaId
        ) {
          let url = getEmployeeVisaById
            .replace("$email", travellerEmail)
            .replace("$id", visaId);
          handleRequest(
            url,
            "GET",
            null,
            (visa) => {
              if (visa) {
                let visaArr = [];
                visaArr.push(visa);
                setVisa(visaArr);
              } else {
                setSnackbarData({
                  message: "Error while fetching employee visa",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
              }
              setIsChildrenRenderEnabled(true);
              handleSetLoading(false);
            },
            (error) => {
              setIsChildrenRenderEnabled(true);
              handleSetLoading(false);
              console.error(error);
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
            }
          );
        } else {
          setIsChildrenRenderEnabled(true);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const handleTabBarChange = (event, newValue) => {
    setTabBarValue(newValue);
  };

  const fetchPerdiemPaymentSummary = () => {
    setIsFetchingPerdiemPaymentSummary(true);
  };

  return (
    <div className="Edit-travel-request">
      <CustomSnackbar snackbar={snackbarData} />

      <div className="Page-title">Manage Travel Request</div>

      <BackdropProgress open={isLoading} />

      {isChildrenRenderEnabled && (
        <div>
          <Accordion
            className="Accordion Border-elevation"
            expanded={travelInfoAccordion}
            onChange={() => setTravelInfoAccordion(!travelInfoAccordion)}
          >
            <AccordionSummary
              className="Accordion-summary"
              expandIcon={<ExpandMoreIcon className="Expand-icon" />}
              style={{ backgroundColor: wso2Theme.palette.primary.light }}
            >
              <span>Travel Information</span>
            </AccordionSummary>
            <AccordionDetails className="Accordion-details">
              <TravelInformation
                travel={travelRequest}
                travelAgents={travelAgent}
                visa={visa}
                fetchData={fetchData}
                isLoading={isLoading}
                setIsLoading={handleSetLoading}
              />
            </AccordionDetails>
          </Accordion>

          {isAccessGranted([
            PRIVILEGES.VIEW_PERDIEMS,
            PRIVILEGES.VIEW_OWN_PERDIEMS,
          ]) && (
            <Accordion
              className="Accordion Border-elevation"
              expanded={perDiemAccordion}
              onChange={() => setPerDiemAccordion(!perDiemAccordion)}
            >
              <AccordionSummary
                className="Accordion-summary"
                expandIcon={<ExpandMoreIcon className="Expand-icon" />}
                style={{ backgroundColor: wso2Theme.palette.primary.light }}
              >
                <span>Per Diems</span>
              </AccordionSummary>
              <AccordionDetails className="Accordion-details">
                <PerdiemInformation
                  travel={travelRequest}
                  travelAgents={travelAgent}
                  visa={visa}
                  fetchData={fetchData}
                  isLoading={isLoading}
                  setIsLoading={handleSetLoading}
                  fetchPerdiemPaymentSummary={fetchPerdiemPaymentSummary}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Travel Invoices Section */}
          {isAccessGranted([
            PRIVILEGES.VIEW_TRAVEL_INVOICES,
            PRIVILEGES.VIEW_OWN_TRAVEL_INVOICES,
          ]) && (
            <Accordion
              className="Accordion Border-elevation"
              expanded={invoiceAccordion}
              onChange={() => setInvoiceAccordion(!invoiceAccordion)}
            >
              <AccordionSummary
                className="Accordion-summary"
                expandIcon={<ExpandMoreIcon className="Expand-icon" />}
                style={{ backgroundColor: wso2Theme.palette.primary.light }}
              >
                <span>Travel Invoices</span>
              </AccordionSummary>
              <AccordionDetails className="Accordion-details">
                <Invoices
                  travel={travelRequest}
                  fetchData={fetchData}
                  isLoading={isLoading}
                  setIsLoading={handleSetLoading}
                  fetchPerdiemPaymentSummary={fetchPerdiemPaymentSummary}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {isAccessGranted([
            PRIVILEGES.VIEW_TRAVEL_CARDS,
            PRIVILEGES.VIEW_OWN_TRAVEL_CARDS,
          ]) && (
            <Accordion
              className="Accordion Border-elevation"
              expanded={travelCardAccordion}
              onChange={() => setTravelCardAccordion(!travelCardAccordion)}
            >
              <AccordionSummary
                className="Accordion-summary"
                expandIcon={<ExpandMoreIcon className="Expand-icon" />}
                style={{ backgroundColor: wso2Theme.palette.primary.light }}
              >
                <span>Travel Cards</span>
              </AccordionSummary>
              <AccordionDetails className="Accordion-details">
                <TravelCard
                  travel={travelRequest}
                  fetchData={fetchData}
                  isLoading={isLoading}
                  setIsLoading={handleSetLoading}
                  fetchPerdiemPaymentSummary={fetchPerdiemPaymentSummary}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {isAccessGranted([
            PRIVILEGES.VIEW_PER_DIEM_PAYMENTS,
            PRIVILEGES.VIEW_OWN_PER_DIEM_PAYMENTS,
          ]) && (
            <Accordion
              className="Accordion Border-elevation"
              expanded={perDiemPaymentsAccordion}
              onChange={() =>
                setPerDiemPaymentsAccordion(!perDiemPaymentsAccordion)
              }
            >
              <AccordionSummary
                className="Accordion-summary"
                expandIcon={<ExpandMoreIcon className="Expand-icon" />}
                style={{ backgroundColor: wso2Theme.palette.primary.light }}
              >
                <span>Per Diem Payments</span>
              </AccordionSummary>
              <AccordionDetails className="Accordion-details">
                <PerDiemPayment
                  travel={travelRequest}
                  isLoading={isLoading}
                  setIsLoading={handleSetLoading}
                  isFetchingPerdiemPaymentSummary={
                    isFetchingPerdiemPaymentSummary
                  }
                  setIsFetchingPerdiemPaymentSummary={
                    setIsFetchingPerdiemPaymentSummary
                  }
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* To be released Version 2.1.0 */}
          {/* <Accordion className='Accordion Border-elevation' expanded={flightAccordion} onChange={() => setFlightAccordion(!flightAccordion)}>
                    <AccordionSummary className='Accordion-summary' expandIcon={<ExpandMoreIcon className='Expand-icon' />}
                        style={{ backgroundColor: wso2Theme.palette.primary.light }}>
                        <span>Flight Bookings</span>
                    </AccordionSummary>
                    <AccordionDetails className='Accordion-details'>
                    </AccordionDetails>
                </Accordion> */}
          {/* {isAccessGranted([PRIVILEGES.GET_HOTEL_BOOKINGS]) && <Accordion className='Accordion Border-elevation' expanded={hotelAccordion} onChange={() => setHotelAccordion(!hotelAccordion)}>
                    <AccordionSummary className='Accordion-summary' expandIcon={<ExpandMoreIcon className='Expand-icon' />}
                        style={{ backgroundColor: wso2Theme.palette.primary.light }}>
                        <span>Hotel Bookings</span>
                    </AccordionSummary>
                    <AccordionDetails className='Accordion-details'>
                        <HotelBooking travel={travelRequest} />
                    </AccordionDetails>
                </Accordion>} */}

          {/* <Accordion className='Accordion Border-elevation' expanded={perdiemAccordion} onChange={() => setPerdiemAccordion(!perdiemAccordion)}>
                    <AccordionSummary className='Accordion-summary' expandIcon={<ExpandMoreIcon className='Expand-icon' />}
                        style={{ backgroundColor: wso2Theme.palette.primary.light }}>
                        <span>Perdiem</span>
                    </AccordionSummary>
                    <AccordionDetails className='Accordion-details'>
                        <Costs travel={travelRequest} />
                    </AccordionDetails>
                </Accordion> */}
        </div>
      )}
    </div>
  );
};

export default ManageTravelRequest;
